import * as Sentry from "@sentry/react"
import { useCallback } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import type { NextStepResponse } from "@/api/api"
import {
  getMissingVerifications,
  isVerificationIncompleteError,
} from "@/api/errors"

// Page of a verification step, e.g. "card_id" -> "card", "bank_info" -> "bank"
export const verificationRoute = (method: string): string =>
  method.split("_")[0]

// The severance flow only has pages for these steps
const severanceRoutes: Record<string, string> = {
  rights: "documents",
  bank_info: "bank",
}

export const severanceVerificationRoute = (
  method: string,
): string | undefined => severanceRoutes[method]

export type VerificationNoticeState = { notice?: string }

/**
 * Returns an error handler that sends the signee to the page of the first
 * missing verification step, passing the error message along as a notice.
 * It returns true when it navigated.
 *
 * It does nothing when the error isn't a missing verification, the step has
 * no page, or the signee is already on it. The caller then shows the error as
 * usual.
 */
export const useVerificationRedirect = (
  basePath: string,
  toRoute: (method: string) => string | undefined,
) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  return useCallback(
    (error: Error): boolean => {
      if (!isVerificationIncompleteError(error)) {
        return false
      }

      const missing = getMissingVerifications(error)
      const route = missing[0] ? toRoute(missing[0]) : undefined
      const path = `${basePath}/${route}`

      if (!route || path === pathname) {
        return false
      }

      Sentry.addBreadcrumb({
        category: "verification",
        message: `Sent back to the ${missing[0]} step`,
        level: "info",
        data: { from: pathname, to: path, missing },
      })

      const state: VerificationNoticeState = { notice: error.message }
      navigate(path, { state })
      return true
    },
    [basePath, navigate, pathname, toRoute],
  )
}

/**
 * Returns a handler that follows a /continue response: the page of the next
 * step, the in-app payment or signature page, or an external signing URL.
 */
export const useNextStepRedirect = (basePath: string) => {
  const navigate = useNavigate()

  return useCallback(
    (res: NextStepResponse) => {
      if (res.next_step) {
        navigate(`${basePath}/${verificationRoute(res.next_step)}`)
      } else if (
        res.redirect_url === "signature" ||
        res.redirect_url === "payment"
      ) {
        navigate(`${basePath}/${res.redirect_url}`)
      } else if (res.redirect_url) {
        window.location.href = res.redirect_url
      }
    },
    [basePath, navigate],
  )
}
